import type { LocaleTextJson } from "./localeText";
import {
	mapProduct,
	type Product,
	type ProductJson,
	type ProductV2Json,
} from "./Product";

export interface ProductTemplateJson {
	id: number;
	nrOfDaysValid: number | null;
	name: LocaleTextJson;
	nrOfZonesGetAll: number;
	ticketType: string;
	zoneType: string;
	products: ProductJson[];
	description: LocaleTextJson | null;
	nrOfMinutesValid: Array<{
		nrOfMinutesValid: number;
		nrOfZones: number;
	}> | null;
	maxNrPassengersPrProduct: number;
	maxTotalNrOfPassengers: number;
	zoneTypeCommon: string;
	ticketTypeCommon: string;
	actionId: number;
	mediaTypes: string[];
	tags?: string[] | null;
}

export interface ProductTemplateV2Json {
	actionId: number;
	description: LocaleTextJson | null;
	id: string;
	maxNrPassengersPrProduct: number | null;
	maxTotalNrOfPassengers: number | null;
	mediaTypes: string[];
	name: LocaleTextJson;
	nrOfDaysValid: number | null;
	nrOfMinutesValid: Array<{
		nrOfMinutesValid: number;
		nrOfZones: number;
	}> | null;
	nrOfZonesGetAll: number | null;
	products: ProductV2Json[];
	ticketType: string;
	ticketTypeCommon: string;
	zoneType: string;
	zoneTypeCommon: string;
	productIdV2: string;
	updatedAt: string;
	token: string;
	productTemplateIdV1: number | null;
	tags: string[] | null;
}

export interface ProductTemplate {
	id: string;
	products: Product[];
	version: "v1" | "v2";
	tags: string[];
}

export const mapProductTemplate = (
	json: ProductTemplateJson | ProductTemplateV2Json,
): ProductTemplate => {
	const id = "token" in json ? json.token : json.id.toString();
	return {
		id,
		products: json.products.map((product) => mapProduct(product)),
		version: "token" in json ? "v2" : "v1",
		tags: json.tags ? json.tags : [],
	};
};

export const mapProductTemplateArray = (
	json: Array<ProductTemplateJson | ProductTemplateV2Json>,
): ProductTemplate[] => json.map(mapProductTemplate);

export const getRuterProductTemplateReponse = (): ProductTemplateV2Json[] => [
	{
		id: "25",
		token: "bd27f8ad-ec2f-4d12-83ea-58bb59d4448d",
		productIdV2: "f33fa072-5b6c-4435-b1fb-05f7834cafe8",
		productTemplateIdV1: 25,
		updatedAt: "2025-01-06T12:46:16.529619791+01:00",
		name: {
			nb: "Bedriftsbillett*",
			en: "Company ticket*",
		},
		description: {
			nb: "",
			en: null,
		},
		nrOfZonesGetAll: null,
		nrOfDaysValid: 365,
		nrOfMinutesValid: null,
		maxNrPassengersPrProduct: null,
		maxTotalNrOfPassengers: null,
		zoneTypeCommon: "ZONE_TO_ZONE",
		ticketTypeCommon: "MULTIPLE_JOURNEY",
		actionId: 0,
		mediaTypes: ["MOBILE_TICKET"],
		tags: [
			"system:sort:merchant:7",
			"system:sort:mobile:4",
			"bigcustomer.subscriptionTicket",
			"bigcustomer:subscriptionTicket",
			"ruterapp:devicechange:10",
			"tetapp:devicechange:10",
		],
		zoneType: "ZoneToZone",
		ticketType: "MultipleJourneyTicket",
		products: [
			{
				id: "22201",
				productIdV1: 22201,
				productTemplateIdV1: 25,
				compoundId:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8|535bce12-3f20-46c9-a2f3-c89c165292b5|ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				compoundIdAlternate:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8-535bce12-3f20-46c9-a2f3-c89c165292b5-ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileId: "ADULT",
				profileIdV1: 1,
				serviceProfileId: "535bce12-3f20-46c9-a2f3-c89c165292b5",
				userDetailId:
					"ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileName: {
					nb: "Voksen",
					en: "Adult",
				},
				profileNamePlural: {
					nb: "Voksne",
					en: "Adults",
				},
				profileDescription: {
					nb: "Voksen",
					en: "Adult",
				},
				nrOfZonesGetAll: null,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: {
					zoneListMustEqual: ["RUT:TariffZone:227"],
					zoneListMustBeSupersetOf: ["RUT:TariffZone:227"],
					zoneListMustBeSubsetOf: ["RUT:TariffZone:227"],
				},
				mediaTypes: ["MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 7780.0,
						priceExclVAT: 6946.43,
						vat: 833.57,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
				],
			},
			{
				id: "22202",
				productIdV1: 22202,
				productTemplateIdV1: 25,
				compoundId:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8|535bce12-3f20-46c9-a2f3-c89c165292b5|c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				compoundIdAlternate:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8-535bce12-3f20-46c9-a2f3-c89c165292b5-c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileId: "SENIOR",
				profileIdV1: 4,
				serviceProfileId: "535bce12-3f20-46c9-a2f3-c89c165292b5",
				userDetailId:
					"c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileName: {
					nb: "Honnør",
					en: "Senior",
				},
				profileNamePlural: {
					nb: "Honnør",
					en: "Seniors",
				},
				profileDescription: {
					nb: "Honnør over 67",
					en: "Senior over 67",
				},
				nrOfZonesGetAll: null,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: {
					zoneListMustEqual: ["RUT:TariffZone:227"],
					zoneListMustBeSupersetOf: ["RUT:TariffZone:227"],
					zoneListMustBeSubsetOf: ["RUT:TariffZone:227"],
				},
				mediaTypes: ["MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 3890.0,
						priceExclVAT: 3473.21,
						vat: 416.79,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
				],
			},
			{
				id: "22201",
				productIdV1: 22201,
				productTemplateIdV1: 25,
				compoundId:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8|12f0174d-610c-425f-a785-1a5bf83a8d57|ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				compoundIdAlternate:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8-12f0174d-610c-425f-a785-1a5bf83a8d57-ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileId: "ADULT",
				profileIdV1: 1,
				serviceProfileId: "12f0174d-610c-425f-a785-1a5bf83a8d57",
				userDetailId:
					"ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileName: {
					nb: "Voksen",
					en: "Adult",
				},
				profileNamePlural: {
					nb: "Voksne",
					en: "Adults",
				},
				profileDescription: {
					nb: "Voksen",
					en: "Adult",
				},
				nrOfZonesGetAll: 3,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: null,
				mediaTypes: ["MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 9850.0,
						priceExclVAT: 8794.64,
						vat: 1055.36,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
					{
						priceInclVAT: 17450.0,
						priceExclVAT: 15580.36,
						vat: 1869.64,
						vatPercent: 12.0,
						nrOfZones: 2,
					},
					{
						priceInclVAT: 25100.0,
						priceExclVAT: 22410.71,
						vat: 2689.29,
						vatPercent: 12.0,
						nrOfZones: 3,
					},
				],
			},
			{
				id: "22202",
				productIdV1: 22202,
				productTemplateIdV1: 25,
				compoundId:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8|12f0174d-610c-425f-a785-1a5bf83a8d57|c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				compoundIdAlternate:
					"f33fa072-5b6c-4435-b1fb-05f7834cafe8-12f0174d-610c-425f-a785-1a5bf83a8d57-c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileId: "SENIOR",
				profileIdV1: 4,
				serviceProfileId: "12f0174d-610c-425f-a785-1a5bf83a8d57",
				userDetailId:
					"c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileName: {
					nb: "Honnør",
					en: "Senior",
				},
				profileNamePlural: {
					nb: "Honnør",
					en: "Seniors",
				},
				profileDescription: {
					nb: "Honnør over 67",
					en: "Senior over 67",
				},
				nrOfZonesGetAll: 3,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: null,
				mediaTypes: ["MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 4930.0,
						priceExclVAT: 4401.79,
						vat: 528.21,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
					{
						priceInclVAT: 8730.0,
						priceExclVAT: 7794.64,
						vat: 935.36,
						vatPercent: 12.0,
						nrOfZones: 2,
					},
					{
						priceInclVAT: 12555.0,
						priceExclVAT: 11209.82,
						vat: 1345.18,
						vatPercent: 12.0,
						nrOfZones: 3,
					},
				],
			},
		],
	},
	{
		id: "4",
		token: "beafb546-dd46-488b-9d11-740a2e5935bc",
		productIdV2: "3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef",
		productTemplateIdV1: 4,
		updatedAt: "2025-01-10T15:01:40.417819134+01:00",
		name: {
			nb: "30-dagersbillett*",
			en: "30-day ticket*",
		},
		description: {
			nb: "",
			en: null,
		},
		nrOfZonesGetAll: null,
		nrOfDaysValid: 30,
		nrOfMinutesValid: null,
		maxNrPassengersPrProduct: null,
		maxTotalNrOfPassengers: null,
		zoneTypeCommon: "ZONE_TO_ZONE",
		ticketTypeCommon: "MULTIPLE_JOURNEY",
		actionId: 0,
		mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
		tags: [
			"system:sort:merchant:6",
			"apita.actionid:32",
			"apita.actionid:42",
			"ruterapp:category:pickup",
			"ruterapp:category:period",
			"apita.exclude:fb3e4d45-e24a-403b-9d41-36ce7a810e41",
			"tetapp:category:period",
			"tetapp:category:pickup",
			"tetapp:category:period:30d",
			"system:sort:mobile:4",
			"bigcustomer:subscriptionComparisonTicket",
		],
		zoneType: "ZoneToZone",
		ticketType: "MultipleJourneyTicket",
		products: [
			{
				id: "21051",
				productIdV1: 21051,
				productTemplateIdV1: 4,
				compoundId:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef|d1fa9d3a-fa69-4ecf-a0a9-dd71eb8fdc8f|ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				compoundIdAlternate:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef-d1fa9d3a-fa69-4ecf-a0a9-dd71eb8fdc8f-ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileId: "ADULT",
				profileIdV1: 1,
				serviceProfileId: "d1fa9d3a-fa69-4ecf-a0a9-dd71eb8fdc8f",
				userDetailId:
					"ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileName: {
					nb: "Voksen",
					en: "Adult",
				},
				profileNamePlural: {
					nb: "Voksne",
					en: "Adults",
				},
				profileDescription: {
					nb: "Voksen",
					en: "Adult",
				},
				nrOfZonesGetAll: null,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: {
					zoneListMustEqual: ["RUT:TariffZone:227"],
					zoneListMustBeSupersetOf: ["RUT:TariffZone:227"],
					zoneListMustBeSubsetOf: ["RUT:TariffZone:227"],
				},
				mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 778.0,
						priceExclVAT: 694.64,
						vat: 83.36,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
				],
			},
			{
				id: "21086",
				productIdV1: 21086,
				productTemplateIdV1: 4,
				compoundId:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef|d1fa9d3a-fa69-4ecf-a0a9-dd71eb8fdc8f|c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				compoundIdAlternate:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef-d1fa9d3a-fa69-4ecf-a0a9-dd71eb8fdc8f-c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileId: "SENIOR",
				profileIdV1: 4,
				serviceProfileId: "d1fa9d3a-fa69-4ecf-a0a9-dd71eb8fdc8f",
				userDetailId:
					"c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileName: {
					nb: "Honnør",
					en: "Senior",
				},
				profileNamePlural: {
					nb: "Honnør",
					en: "Seniors",
				},
				profileDescription: {
					nb: "Honnør over 67",
					en: "Senior over 67",
				},
				nrOfZonesGetAll: null,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: {
					zoneListMustEqual: ["RUT:TariffZone:227"],
					zoneListMustBeSupersetOf: ["RUT:TariffZone:227"],
					zoneListMustBeSubsetOf: ["RUT:TariffZone:227"],
				},
				mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 389.0,
						priceExclVAT: 347.32,
						vat: 41.68,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
				],
			},
			{
				id: "21051",
				productIdV1: 21051,
				productTemplateIdV1: 4,
				compoundId:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef|ba3a4b83-dc09-4c59-ae05-3be3d4a3380a|ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				compoundIdAlternate:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef-ba3a4b83-dc09-4c59-ae05-3be3d4a3380a-ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileId: "ADULT",
				profileIdV1: 1,
				serviceProfileId: "ba3a4b83-dc09-4c59-ae05-3be3d4a3380a",
				userDetailId:
					"ec2e5124719b87b12c8b7c2567230e083efa3ea4cd7b38600bb2a70baef92992",
				profileName: {
					nb: "Voksen",
					en: "Adult",
				},
				profileNamePlural: {
					nb: "Voksne",
					en: "Adults",
				},
				profileDescription: {
					nb: "Voksen",
					en: "Adult",
				},
				nrOfZonesGetAll: 3,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: null,
				mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 985.0,
						priceExclVAT: 879.46,
						vat: 105.54,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
					{
						priceInclVAT: 1745.0,
						priceExclVAT: 1558.04,
						vat: 186.96,
						vatPercent: 12.0,
						nrOfZones: 2,
					},
					{
						priceInclVAT: 2510.0,
						priceExclVAT: 2241.07,
						vat: 268.93,
						vatPercent: 12.0,
						nrOfZones: 3,
					},
				],
			},
			{
				id: "21076",
				productIdV1: 21076,
				productTemplateIdV1: 4,
				compoundId:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef|ba3a4b83-dc09-4c59-ae05-3be3d4a3380a|b99bd5fe516825b781986da9f6bf16473c7a90c20377876007157dfbe41a6f9e",
				compoundIdAlternate:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef-ba3a4b83-dc09-4c59-ae05-3be3d4a3380a-b99bd5fe516825b781986da9f6bf16473c7a90c20377876007157dfbe41a6f9e",
				profileId: "CHILD",
				profileIdV1: 2,
				serviceProfileId: "ba3a4b83-dc09-4c59-ae05-3be3d4a3380a",
				userDetailId:
					"b99bd5fe516825b781986da9f6bf16473c7a90c20377876007157dfbe41a6f9e",
				profileName: {
					nb: "Barn",
					en: "Child",
				},
				profileNamePlural: {
					nb: "Barn",
					en: "Children",
				},
				profileDescription: {
					nb: "Barn 6-17 år",
					en: "Children 6-17 år",
				},
				nrOfZonesGetAll: 3,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: null,
				mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 327.0,
						priceExclVAT: 291.96,
						vat: 35.04,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
					{
						priceInclVAT: 436.0,
						priceExclVAT: 389.29,
						vat: 46.71,
						vatPercent: 12.0,
						nrOfZones: 2,
					},
					{
						priceInclVAT: 545.0,
						priceExclVAT: 486.61,
						vat: 58.39,
						vatPercent: 12.0,
						nrOfZones: 3,
					},
				],
			},
			{
				id: "21086",
				productIdV1: 21086,
				productTemplateIdV1: 4,
				compoundId:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef|ba3a4b83-dc09-4c59-ae05-3be3d4a3380a|c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				compoundIdAlternate:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef-ba3a4b83-dc09-4c59-ae05-3be3d4a3380a-c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileId: "SENIOR",
				profileIdV1: 4,
				serviceProfileId: "ba3a4b83-dc09-4c59-ae05-3be3d4a3380a",
				userDetailId:
					"c32778ebf6de3ae1be103b1497695be928b9c4210ef43cedece775de3cae2b1f",
				profileName: {
					nb: "Honnør",
					en: "Senior",
				},
				profileNamePlural: {
					nb: "Honnør",
					en: "Seniors",
				},
				profileDescription: {
					nb: "Honnør over 67",
					en: "Senior over 67",
				},
				nrOfZonesGetAll: 3,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: null,
				mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 493.0,
						priceExclVAT: 440.18,
						vat: 52.82,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
					{
						priceInclVAT: 873.0,
						priceExclVAT: 779.46,
						vat: 93.54,
						vatPercent: 12.0,
						nrOfZones: 2,
					},
					{
						priceInclVAT: 1255.0,
						priceExclVAT: 1120.54,
						vat: 134.46,
						vatPercent: 12.0,
						nrOfZones: 3,
					},
				],
			},
			{
				id: "21081",
				productIdV1: 21081,
				productTemplateIdV1: 4,
				compoundId:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef|ba3a4b83-dc09-4c59-ae05-3be3d4a3380a|810e75d668f86b33cfe763864b903086bfbe1271299be1ce75c8ea0088182c6b",
				compoundIdAlternate:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef-ba3a4b83-dc09-4c59-ae05-3be3d4a3380a-810e75d668f86b33cfe763864b903086bfbe1271299be1ce75c8ea0088182c6b",
				profileId: "STUDENT",
				profileIdV1: 3,
				serviceProfileId: "ba3a4b83-dc09-4c59-ae05-3be3d4a3380a",
				userDetailId:
					"810e75d668f86b33cfe763864b903086bfbe1271299be1ce75c8ea0088182c6b",
				profileName: {
					nb: "Student",
					en: "Student",
				},
				profileNamePlural: {
					nb: "Stundenter",
					en: "Students",
				},
				profileDescription: {
					nb: "Studenter under 30 år",
					en: "Students under 30",
				},
				nrOfZonesGetAll: 3,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: null,
				mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 591.0,
						priceExclVAT: 527.68,
						vat: 63.32,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
					{
						priceInclVAT: 1047.0,
						priceExclVAT: 934.82,
						vat: 112.18,
						vatPercent: 12.0,
						nrOfZones: 2,
					},
					{
						priceInclVAT: 1506.0,
						priceExclVAT: 1344.64,
						vat: 161.36,
						vatPercent: 12.0,
						nrOfZones: 3,
					},
				],
			},
			{
				id: "21091",
				productIdV1: 21091,
				productTemplateIdV1: 4,
				compoundId:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef|ba3a4b83-dc09-4c59-ae05-3be3d4a3380a|425bc346ab16217b86b407fc9737e7a589054ebe28905d770960894e4e54bf93",
				compoundIdAlternate:
					"3d8c6313-02f0-4a2a-9ad0-3fa2e879a6ef-ba3a4b83-dc09-4c59-ae05-3be3d4a3380a-425bc346ab16217b86b407fc9737e7a589054ebe28905d770960894e4e54bf93",
				profileId: "YOUTH",
				profileIdV1: 32,
				serviceProfileId: "ba3a4b83-dc09-4c59-ae05-3be3d4a3380a",
				userDetailId:
					"425bc346ab16217b86b407fc9737e7a589054ebe28905d770960894e4e54bf93",
				profileName: {
					nb: "Ungdom",
					en: "Youth",
				},
				profileNamePlural: {
					nb: "Ungdommer",
					en: "Youths",
				},
				profileDescription: {
					nb: "Ungdommer på 18 eller 19",
					en: "Youths between 18 and 19",
				},
				nrOfZonesGetAll: 3,
				nrOfMinutesValid: null,
				presetZoneIds: null,
				isCarnetProduct: false,
				calendarRef: null,
				filters: null,
				mediaTypes: ["TRAVEL_CARD", "MOBILE_TICKET"],
				priceVariableAmount: null,
				prices: [
					{
						priceInclVAT: 327.0,
						priceExclVAT: 291.96,
						vat: 35.04,
						vatPercent: 12.0,
						nrOfZones: 1,
					},
					{
						priceInclVAT: 436.0,
						priceExclVAT: 389.29,
						vat: 46.71,
						vatPercent: 12.0,
						nrOfZones: 2,
					},
					{
						priceInclVAT: 545.0,
						priceExclVAT: 486.61,
						vat: 58.39,
						vatPercent: 12.0,
						nrOfZones: 3,
					},
				],
			},
		],
	},
];
